import React from "react";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import {
  Alert,
  Box,
  Container,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { getErrorResponse } from "../utils/util";
import BackBtn from "../widgets/BackBtn";
import { Eye, Plus, FolderOpen, Calendar, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const UnifiedCampaignsList = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/campaign/campaign-groups`
  );

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  const groups = data?.data ?? [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3, pb: 6 }}>
      <BackBtn />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "text.primary", letterSpacing: "-0.5px" }}
          >
            Campaign Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {groups.length} group{groups.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>

        <Tooltip title="Add New Group" arrow>
          <IconButton
            onClick={() => navigate("/home/add-campaign-group")}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              borderRadius: 2,
              p: 1.25,
              "&:hover": { bgcolor: "primary.dark" },
              boxShadow: 2,
            }}
          >
            <Plus size={20} strokeWidth={2.5} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Empty State */}
      {groups.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            border: "1.5px dashed",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "action.hover",
          }}
        >
          <FolderOpen
            size={40}
            strokeWidth={1.2}
            style={{ color: "#9e9e9e", marginBottom: 12 }}
          />
          <Typography variant="body1" fontWeight={600} color="text.secondary">
            No campaign groups yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Click the <strong>+</strong> button to create your first group
          </Typography>
        </Box>
      ) : (
        /* List */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {groups.map((group, idx) => (
            <Box
              key={group._id ?? idx}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 2,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": {
                  boxShadow: "0 3px 10px rgba(0,0,0,0.09)",
                  borderColor: "primary.light",
                },
              }}
            >
              {/* Left: icon + info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "primary.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FolderOpen
                    size={18}
                    strokeWidth={1.8}
                    style={{ color: "#1976d2" }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ color: "text.primary", lineHeight: 1.3 }}
                  >
                    {group.name}
                  </Typography>

                  {/* Date + contact list row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mt: 0.4,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Calendar
                        size={12}
                        strokeWidth={1.8}
                        style={{ color: "#9e9e9e" }}
                      />
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(group.createdOn)}
                      </Typography>
                    </Box>

                    {group.contactList ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Users
                          size={12}
                          strokeWidth={1.8}
                          style={{ color: "#1976d2" }}
                        />
                        <Typography
                          variant="caption"
                          color="primary.main"
                          fontWeight={600}
                        >
                          {group.contactList}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Users
                          size={12}
                          strokeWidth={1.8}
                          style={{ color: "#bbb" }}
                        />
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          fontStyle="italic"
                        >
                          No contact list
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Right: actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="View Details" arrow>
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigate(`/home/${group._id}/campaign-group-detail`)
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      "&:hover": {
                        bgcolor: "primary.50",
                        borderColor: "primary.light",
                      },
                    }}
                  >
                    <Eye size={16} strokeWidth={1.8} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default UnifiedCampaignsList;
